import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import LoginModal from './LoginModal';
import PaymentModal from './PaymentModal';
import { useNativePayment } from '../hooks/useNativePayment';
import stripeConfig from '../../config/stripe';
import './TrialPopup.css';

// Load Inter font async (non-blocking) so TrialPopup doesn't block first paint
function useInterFont() {
  useEffect(() => {
    if (document.getElementById('trial-popup-inter-font')) return;
    const link = document.createElement('link');
    link.id = 'trial-popup-inter-font';
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap';
    link.media = 'print';
    link.onload = function() { link.media = 'all'; };
    document.head.appendChild(link);
  }, []);
}

const TrialPopup = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    useInterFont();
    const { currentUser } = useAuth();
    const { hasActiveSubscription, hasUsedTrial } = useSubscription();
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);

    // Memoize plan config so native payment doesn't re-initialize on every render
    const nativePlanConfig = useMemo(() => ({
        name: 'Monthly Premium',
        priceId: stripeConfig.priceIds.monthlyWithTrial,
        trialDays: hasUsedTrial ? 0 : 3
    }), [hasUsedTrial]);

    // background check for native Google/Apple Pay availability
    const { canMakePayment, triggerNativePayment, clientSecret, isReady, isFetching } = useNativePayment(
        currentUser,
        nativePlanConfig,
        hasUsedTrial,
        () => { onClose(); navigate('/thank-you?success=true'); },
        (msg) => { 
            console.error('Native payment failed:', msg); 
            setProcessClick(false);
            setShowPaymentModal(true); 
        }
    );

    const [processClick, setProcessClick] = useState(false);

    // When popup is open: lock document scroll and hide the right-side scrollbar.
    // NOTE: We intentionally do NOT set touchAction:'none' on document.body —
    // that would freeze ALL touch input across the page (including close button)
    // and could permanently lock the UI if cleanup is delayed. The overlay element
    // handles touch containment via CSS touch-action instead.
    useEffect(() => {
        if (!isOpen) return;
        const scrollY = window.scrollY;
        const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
        const html = document.documentElement;

        const prevHtmlOverflow = html.style.overflow;
        const prevHtmlPaddingRight = html.style.paddingRight;
        const prevHtmlScrollbarGutter = html.style.scrollbarGutter;
        const prevBodyOverflow = document.body.style.overflow;
        const prevBodyPosition = document.body.style.position;
        const prevBodyTop = document.body.style.top;
        const prevBodyLeft = document.body.style.left;
        const prevBodyRight = document.body.style.right;
        const prevBodyWidth = document.body.style.width;
        const prevBodyPaddingRight = document.body.style.paddingRight;

        html.classList.add('trial-popup-open');
        html.style.overflow = 'hidden';
        html.style.scrollbarGutter = 'auto';
        html.style.paddingRight = `${scrollbarWidth}px`;
        document.body.style.paddingRight = `${scrollbarWidth}px`;
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';

        return () => {
            html.classList.remove('trial-popup-open');
            html.style.overflow = prevHtmlOverflow;
            html.style.scrollbarGutter = prevHtmlScrollbarGutter;
            html.style.paddingRight = prevHtmlPaddingRight;
            document.body.style.overflow = prevBodyOverflow;
            document.body.style.position = prevBodyPosition;
            document.body.style.top = prevBodyTop;
            document.body.style.left = prevBodyLeft;
            document.body.style.right = prevBodyRight;
            document.body.style.width = prevBodyWidth;
            document.body.style.paddingRight = prevBodyPaddingRight;
            window.scrollTo(0, scrollY);
        };
    }, [isOpen]);

    // This plan is specifically for the 3-day trial
    const trialPlan = {
        name: 'Monthly Premium',
        price: '$19.99',
        period: '/month',
        priceId: stripeConfig.priceIds.monthlyWithTrial,
        trialDays: hasUsedTrial ? 0 : 3, // This tells PaymentModal and Functions to apply the trial
        description: hasUsedTrial ? 'Monthly Subscription' : 'Monthly Subscription with 3-days free trial',
        features: [
            { text: 'Access to Unlimited Flights', included: true },
            { text: 'Track Without Limits', included: true },
            { text: 'Track Any Flight, Anywhere', included: true },
            { text: 'Instant Flight Updates', included: true },
            { text: '24/7 Customer Support', included: true }
        ]
    };

    const handleTrialClick = async () => {
        if (!currentUser || processClick) {
            if (!currentUser) {
                const separator = window.location.search ? '&' : '?';
                const returnUrl = window.location.pathname + window.location.search + separator + 'openTrial=true';
                navigate('/sign-in?returnTo=' + encodeURIComponent(returnUrl));
            }
            return;
        }

        if (hasActiveSubscription()) {
            return;
        }

        setProcessClick(true);

        // Try native Google/Apple Pay first
        if (isReady && canMakePayment) {
            const success = await triggerNativePayment();
            if (success) {
                // If native sheet is opening, we stay in processing state
                // This prevents the modal from ever waking up while the sheet is active
                setTimeout(() => setProcessClick(false), 5000);
                return; 
            }
        }

        // Fallback to modal
        setProcessClick(false);
        setShowPaymentModal(true);
    };

    const handleLoginSuccess = () => {
        setShowLoginModal(false);
        // After login, show payment modal for the trial
        setShowPaymentModal(true);
    };

    if (!isOpen) return null;

    return (
        <div className="trial-popup-overlay">
            <div className="trial-popup-container">
                <button className="trial-popup-close" aria-label="Close">
                    <svg className='close-trial' onClick={onClose} xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 23 23" fill="none">
                        <path d="M12.2483 11.1576L16.5692 6.94718C16.6348 6.88546 16.6871 6.81164 16.7231 6.73001C16.759 6.64838 16.778 6.56059 16.7788 6.47175C16.7796 6.38291 16.7622 6.29481 16.7277 6.21259C16.6932 6.13036 16.6422 6.05566 16.5777 5.99284C16.5132 5.93002 16.4366 5.88034 16.3522 5.8467C16.2678 5.81306 16.1774 5.79613 16.0862 5.7969C15.995 5.79767 15.9049 5.81613 15.8212 5.85119C15.7374 5.88626 15.6616 5.93723 15.5983 6.00113L11.2773 10.2114L6.95642 6.00113C6.82718 5.87797 6.65331 5.80943 6.47253 5.8104C6.29176 5.81137 6.11867 5.88177 5.99083 6.00631C5.86299 6.13086 5.79071 6.2995 5.78969 6.47565C5.78866 6.65179 5.85896 6.82123 5.98534 6.94718L10.3064 11.1576L5.98534 15.368C5.92041 15.4299 5.86873 15.5037 5.83328 15.5851C5.79783 15.6666 5.77932 15.7541 5.77882 15.8426C5.77832 15.9311 5.79584 16.0188 5.83037 16.1007C5.86489 16.1825 5.91574 16.2569 5.97996 16.3194C6.04419 16.382 6.12051 16.4315 6.20451 16.4652C6.28852 16.4988 6.37853 16.5159 6.46935 16.5153C6.56017 16.5148 6.64998 16.4968 6.73358 16.4622C6.81719 16.4277 6.89293 16.3773 6.95642 16.314L11.2773 12.1038L15.5983 16.314C15.7323 16.4447 15.9081 16.5101 16.0837 16.5101C16.2593 16.5101 16.4353 16.4447 16.5692 16.314C16.6979 16.1886 16.7702 16.0184 16.7702 15.841C16.7702 15.6636 16.6979 15.4934 16.5692 15.368L12.2483 11.1576Z" fill="black" fillOpacity="0.4" />
                    </svg>
                </button>

                <div className="trial-popup-content">
                    <div className="trial-header-section">
                        <div className="trial-badge">
                            <span className="crown-icon"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M15.3452 6.12233L13.6327 12.7148C13.5828 12.9057 13.4712 13.0748 13.3152 13.1955C13.1591 13.3163 12.9675 13.382 12.7702 13.3823H3.23017C3.03286 13.382 2.84122 13.3163 2.68518 13.1955C2.52915 13.0748 2.4175 12.9057 2.36767 12.7148L0.655171 6.12233C0.613518 5.96365 0.616487 5.79654 0.66375 5.63944C0.711013 5.48233 0.800735 5.34132 0.923027 5.23196C1.04532 5.1226 1.19543 5.04912 1.35682 5.01963C1.51821 4.99014 1.68461 5.00578 1.83767 5.06483L4.67517 6.15733C4.70387 6.16849 4.73539 6.17014 4.76509 6.16204C4.7948 6.15394 4.82111 6.13651 4.84017 6.11233L7.29767 2.96233C7.38059 2.85493 7.48703 2.76797 7.60882 2.70813C7.7306 2.6483 7.86448 2.61719 8.00017 2.61719C8.13586 2.61719 8.26974 2.6483 8.39152 2.70813C8.51331 2.76797 8.61975 2.85493 8.70267 2.96233L11.1602 6.11233C11.1792 6.13651 11.2055 6.15394 11.2353 6.16204C11.265 6.17014 11.2965 6.16849 11.3252 6.15733L14.1627 5.06483C14.3157 5.00834 14.4812 4.9946 14.6414 5.02507C14.8016 5.05555 14.9505 5.1291 15.0721 5.23782C15.1936 5.34654 15.2833 5.48633 15.3314 5.64216C15.3795 5.798 15.3843 5.964 15.3452 6.12233Z" fill="url(#paint0_linear_5647_19383)" />
                                <defs>
                                    <linearGradient id="paint0_linear_5647_19383" x1="0.625671" y1="7.99983" x2="15.3794" y2="7.99983" gradientUnits="userSpaceOnUse">
                                        <stop stopColor="#FFDE00" />
                                        <stop offset="1" stopColor="#FD5900" />
                                    </linearGradient>
                                </defs>
                            </svg></span>
                            <span className="upgrade-text">UPGRADE TO PREMIUM</span>
                        </div>

                        <h2 className="trial-title">Full Flight Access & 3D View</h2>
                    </div>

                    <div className="trial-scrollable-section">
                        <div className="trial-image-container">
                            <video
                                src="https://miczon.sgp1.cdn.digitaloceanspaces.com/LiveFlightTracker%20Assets/Live%20Flights%20Tracker%20%E2%80%93%20Real-Time%20Global%20Flight%20Radar%20_%20Flight%20Status.mp4"
                                width={400}
                                height={220}
                                autoPlay
                                muted
                                loop
                                playsInline
                                preload="none"
                                className="trial-popup-video"
                                aria-label="3D Flight View"
                            />
                        </div>

                        {/* Comparison Table */}
                        <table className="comparison-table">
                            <thead>
                                <tr>
                                    <th className="col-benefits">Benefits</th>
                                    <th className="col-basic">Basic</th>
                                    <th className="col-premium">Premium</th>
                                </tr>
                            </thead>
                            <tbody>
                                {trialPlan.features.map((feature, index) => (
                                    <tr key={index}>
                                        <td className="col-benefits">{feature.text}</td>
                                        <td className="col-basic">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                <line x1="5.27814" y1="8.72186" x2="15.7219" y2="8.72186" stroke="#7B8096" strokeWidth="2.55628" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </td>
                                        <td className="col-premium">
                                            <span className="green-checkmark">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="12" viewBox="0 0 16 12" fill="none">
                                                    <path d="M14.6114 1.27734L5.44474 10.444L1.27808 6.27734" stroke="#34C658" strokeWidth="2.55628" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="trial-fixed-bottom-section">
                        <button 
                            className="trial-button" 
                            onClick={handleTrialClick}
                            disabled={processClick || isFetching}
                            style={{
                                opacity: (processClick || isFetching) ? 0.7 : 1,
                                cursor: (processClick || isFetching) ? 'wait' : 'pointer'
                            }}
                        >
                            {processClick ? 'Processing...' : (isFetching ? 'Preparing...' : (hasUsedTrial ? 'Continue' : 'Start Free Trial'))}
                        </button>

                        <div className="no-payment-badge">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <circle cx="12.8306" cy="12.8335" r="4.98193" fill="white" />
                                <path d="M20.7911 13.9158C20.3993 13.3064 20.0078 12.7188 19.616 12.1096C19.5291 11.979 19.5291 11.892 19.616 11.7614C20.0078 11.174 20.3776 10.5863 20.7694 9.99883C21.2263 9.30256 20.987 8.62781 20.2034 8.34503C19.5508 8.1057 18.898 7.84465 18.2451 7.60531C18.1146 7.56167 18.0493 7.47459 18.0493 7.32234C18.0275 6.60435 17.9841 5.88615 17.9405 5.18988C17.897 4.42824 17.3095 3.99302 16.5696 4.18891C15.8734 4.36288 15.1771 4.55877 14.5023 4.75466C14.3501 4.7981 14.2632 4.75466 14.1542 4.64585C13.719 4.0799 13.262 3.53608 12.827 2.99205C12.3481 2.38286 11.6082 2.38286 11.1078 2.99205C10.6726 3.53608 10.2157 4.0801 9.80217 4.62413C9.69337 4.77638 9.58456 4.7981 9.41059 4.75466C8.73604 4.55877 8.06149 4.3848 7.58262 4.25407C6.64702 4.03646 6.08127 4.40652 6.03763 5.18988C5.99419 5.90787 5.95074 6.62607 5.92882 7.36598C5.92882 7.51823 5.86366 7.58339 5.73313 7.64876L3.70947 8.43212C3.013 8.71509 2.79539 9.38964 3.20889 10.0206C3.60067 10.6299 3.99225 11.2174 4.38403 11.8266C4.47091 11.9573 4.47091 12.0442 4.38403 12.1967C3.97249 12.8049 3.57345 13.4215 3.18717 14.0462C2.81731 14.6338 3.05664 15.3301 3.70927 15.5911C4.38403 15.8522 5.0803 16.1133 5.75485 16.3745C5.9071 16.4181 5.95074 16.505 5.95074 16.6575C5.97246 17.3538 6.05955 18.0285 6.05955 18.7246C6.05955 19.4211 6.66874 20.052 7.51746 19.7909C8.19201 19.5733 8.86657 19.4211 9.54112 19.225C9.67165 19.1815 9.75873 19.2034 9.84581 19.334C10.3028 19.8997 10.738 20.4437 11.1949 21.0095C11.6953 21.6187 12.4135 21.6187 12.8922 21.0095C13.3491 20.4435 13.7843 19.8995 14.2413 19.334C14.3284 19.2252 14.3935 19.1815 14.546 19.2252C15.2422 19.4211 15.9385 19.595 16.635 19.7909C17.353 19.9868 17.9622 19.5516 17.9841 18.8117C18.0275 18.0937 18.071 17.3755 18.0929 16.6356C18.0929 16.4616 18.1798 16.3964 18.3105 16.3528C18.9741 16.1084 19.6342 15.8545 20.2905 15.5911C20.987 15.243 21.2046 14.5469 20.7911 13.9158ZM15.5904 10.3468L11.2384 14.699C11.1296 14.8076 10.9773 14.8947 10.8249 14.9164C10.7814 14.9164 10.7161 14.9383 10.6726 14.9383C10.4767 14.9383 10.2591 14.8512 10.1069 14.6988L8.32254 12.9145C8.17616 12.7669 8.09402 12.5674 8.09402 12.3596C8.09402 12.1517 8.17616 11.9523 8.32254 11.8047C8.47012 11.6583 8.66956 11.5762 8.87743 11.5762C9.08529 11.5762 9.28474 11.6583 9.43232 11.8047L10.6509 13.0233L14.4372 9.237C14.5847 9.09061 14.7842 9.00847 14.992 9.00847C15.1999 9.00847 15.3994 9.09061 15.5469 9.237C15.8951 9.54169 15.8951 10.0419 15.5904 10.3468Z" fill="#0095FF" />
                            </svg>
                            <span>No Payment Now</span>
                        </div>

                        <p className="pricing-note">
                            {hasUsedTrial
                                ? "$19.99/month apply. Auto renews, cancel anytime on Premium Screen"
                                : "3 days free trial and after that $19.99/month apply. Auto renews, cancel anytime on Premium Screen"
                            }
                        </p>

                        <div className="marketing-text">
                            <h3>Unlock the full power of flight tracking.</h3>
                            <p className='marketing-text-para'>
                                Enjoy access to unlimited flights, track without limits, and follow any flight worldwide with instant updates. Stay focused with a premium experience and get peace of mind knowing our 24/7 customer support is always here for you.
                            </p>
                        </div>
                        
                        {!currentUser && (
                            <div className="trial-footer">
                                <span>Already have an account? </span>
                                <span className="login-link" onClick={() => navigate('/sign-in?returnTo=' + encodeURIComponent(window.location.pathname + '?openTrial=true'))}>Log in here</span>
                            </div>
                        )}
                    </div>
                </div>

                <LoginModal
                    isOpen={showLoginModal}
                    onClose={() => setShowLoginModal(false)}
                    onSuccess={handleLoginSuccess}
                />

                {showPaymentModal && (
                    <PaymentModal
                        isOpen={showPaymentModal}
                        onClose={() => setShowPaymentModal(false)}
                        selectedPlan={trialPlan}
                        initialClientSecret={clientSecret}
                        onSuccess={() => { 
                            setShowPaymentModal(false); 
                            onClose();
                            navigate('/thank-you?success=true'); 
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default TrialPopup;
